import { z } from 'zod';
import { LanguageCodeSchema } from './common';

/**
 * Readium WebPub Manifest — szigorú TypeScript verzió.
 * Spec: https://readium.org/webpub-manifest/
 *
 * A manifest-svc állítja elő, az olvasó BFF szolgálja ki cache-elve.
 */

export const LinkSchema = z.object({
  href: z.string(),
  rel: z.array(z.string()).or(z.string()).optional(),
  type: z.string().optional(),
  title: z.string().optional(),
  templated: z.boolean().optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
  height: z.number().int().optional(),
  width: z.number().int().optional(),
  duration: z.number().optional(),
  bitrate: z.number().optional(),
  language: LanguageCodeSchema.optional(),
  alternate: z.lazy((): z.ZodTypeAny => z.array(LinkSchema)).optional(),
  children: z.lazy((): z.ZodTypeAny => z.array(LinkSchema)).optional(),
});
export type Link = z.infer<typeof LinkSchema>;

export const ContributorMetaSchema = z.union([
  z.string(),
  z.object({
    name: z.union([z.string(), z.record(z.string(), z.string())]),
    sortAs: z.string().optional(),
    identifier: z.string().optional(),
    role: z.union([z.string(), z.array(z.string())]).optional(),
  }),
]);

export const MetadataSchema = z.object({
  '@type': z.string().optional(),
  title: z.union([z.string(), z.record(z.string(), z.string())]),
  subtitle: z.union([z.string(), z.record(z.string(), z.string())]).optional(),
  identifier: z.string().optional(),
  language: z.union([LanguageCodeSchema, z.array(LanguageCodeSchema)]).optional(),
  modified: z.string().datetime({ offset: true }).optional(),
  published: z.string().datetime({ offset: true }).optional(),
  author: z.union([ContributorMetaSchema, z.array(ContributorMetaSchema)]).optional(),
  translator: z.union([ContributorMetaSchema, z.array(ContributorMetaSchema)]).optional(),
  editor: z.union([ContributorMetaSchema, z.array(ContributorMetaSchema)]).optional(),
  publisher: z.union([ContributorMetaSchema, z.array(ContributorMetaSchema)]).optional(),
  description: z.string().optional(),
  numberOfPages: z.number().int().positive().optional(),
  duration: z.number().positive().optional(),
  // Readium presentation hints
  presentation: z
    .object({
      layout: z.enum(['fixed', 'reflowable']).optional(),
      orientation: z.enum(['auto', 'portrait', 'landscape']).optional(),
      spread: z.enum(['auto', 'none', 'both', 'landscape']).optional(),
      overflow: z.enum(['auto', 'paginated', 'scrolled']).optional(),
    })
    .optional(),
});
export type WebPubMetadata = z.infer<typeof MetadataSchema>;

export const WebPubManifestSchema = z.object({
  '@context': z.union([z.string(), z.array(z.string())]).default('https://readium.org/webpub-manifest/context.jsonld'),
  metadata: MetadataSchema,
  links: z.array(LinkSchema).default([]),
  readingOrder: z.array(LinkSchema).min(1),
  resources: z.array(LinkSchema).default([]),
  toc: z.array(LinkSchema).optional(),
  landmarks: z.array(LinkSchema).optional(),
  pageList: z.array(LinkSchema).optional(),
  guided: z.array(LinkSchema).optional(),
});
export type WebPubManifest = z.infer<typeof WebPubManifestSchema>;
